import {
  X,
  Edit,
  CheckCircle2,
  FileText,
  Star,
  Package,
  Coins,
  Clock,
  Tag,
} from 'lucide-react'
import type { AIService } from '@/lib/supabase-client'
import { getCategory } from '@/constants/service-categories'
import { ServiceLogo } from '@/components/ServiceLogo'

interface ServiceDetailModalProps {
  service: AIService | null
  isOpen: boolean
  onClose: () => void
  onEdit: (service: AIService, e: React.MouseEvent) => void
  recentLogs: any[]
  loadingLogs: boolean
}

export function ServiceDetailModal({
  service,
  isOpen,
  onClose,
  onEdit,
  recentLogs,
  loadingLogs
}: ServiceDetailModalProps) {
  if (!isOpen || !service) return null

  const categoryInfo = service.category ? getCategory(service.category) : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ServiceLogo
              provider={service.provider}
              name={service.name}
              size="lg"
              className="bg-white/20 backdrop-blur-sm rounded-lg p-1.5"
            />
            <div>
              <h3 className="text-lg font-bold">{service.name}</h3>
              <p className="text-sm text-blue-100">{service.provider}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Description and Category */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
              <div className="flex items-center gap-1.5">
                <Tag className="w-4 h-4 text-purple-500" />
                <span className="font-medium">{categoryInfo ? categoryInfo.name : '未分类'}</span>
              </div>
              <span className="text-gray-400">•</span>
              <span>{service.description || '无描述'}</span>
            </div>
            {service.detailed_description && (
              <p className="text-sm text-gray-700 leading-relaxed mt-2 pt-2 border-t border-blue-200">
                {service.detailed_description}
              </p>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center gap-2 text-yellow-600 mb-2">
                <Coins className="w-5 h-5" />
                <span className="text-xs font-medium">积分消耗</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{service.points_cost}</div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <Clock className="w-5 h-5" />
                <span className="text-xs font-medium">超时设置</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{service.timeout_seconds}<span className="text-sm text-gray-500 ml-1">秒</span></div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center gap-2 text-purple-600 mb-2">
                <FileText className="w-5 h-5" />
                <span className="text-xs font-medium">最大 Tokens</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">{service.max_tokens}</div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center gap-2 text-green-600 mb-2">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-xs font-medium">运行状态</span>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${
                service.status === 'active'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  service.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                }`} />
                {service.status === 'active' ? '启用中' : '已禁用'}
              </span>
            </div>
          </div>

          {/* Additional Info */}
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center gap-2 text-sm">
              <Package className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">服务 ID:</span>
              <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200">{service.id}</code>
            </div>
          </div>

          {/* Features */}
          {service.features && service.features.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Star className="w-4 h-4" />
                功能特性
              </h4>
              <div className="flex flex-wrap gap-2">
                {service.features.map((feature, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 text-sm rounded-lg border border-blue-200"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recent Logs */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                最近调用记录
              </h4>
              {loadingLogs && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                  加载中...
                </div>
              )}
            </div>

            {recentLogs.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">时间</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">用户</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">状态</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-700">消耗积分</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-700">响应时间</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {recentLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600">
                          {new Date(log.created_at).toLocaleString('zh-CN')}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-mono text-xs">
                          {log.user_email || log.user_id?.substring(0, 8) + '...'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                            log.status_code >= 200 && log.status_code < 300
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {log.status_code >= 200 && log.status_code < 300 ? (
                              <CheckCircle2 className="w-3 h-3" />
                            ) : (
                              <X className="w-3 h-3" />
                            )}
                            {log.status_code}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-900 font-medium">
                          {log.points_consumed || 0}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">暂无调用记录</p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            关闭
          </button>
          <button
            onClick={(e) => onEdit(service, e)}
            className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            编辑服务
          </button>
        </div>
      </div>
    </div>
  )
}
